import {
  Button,
  Buttons,
  CancelButton,
  Checkbox,
  Drawer,
  Field,
  Form,
  FormMessage,
  Radio,
  RadioGroup,
  Table,
} from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useActions, useDirtyCheck, useForm } from '~/hooks';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { emptyStringToNull, mergeValues } from '~/utils';
import { colors } from '~/styles';
import * as Yup from 'yup';

const initialState = { isReady: false, opportunityTypes: null, recordTypes: null };
const handlers = {
  ready: ({ opportunityTypes, recordTypes }) => ({ isReady: true, opportunityTypes, recordTypes }),
};

const StyledCheckbox = styled.div`
  > label > div {
    background: ${colors.white};
  }
`;

export default function SyncSettingsDrawer({ integration, onClose, onSave }) {
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting }, form] = useForm();
  const [{ isReady, opportunityTypes, recordTypes }, actions] = useActions(handlers, initialState);
  const api = useApi();
  const { workspace } = useWorkspace();

  useEffect(() => {
    (async () => {
      const { data: recordTypes } = await api.www.workspaces(workspace.id).salesforce.getPipelines();
      const { data: opportunityTypes } = await api.www.workspaces(workspace.id).salesforce.getOpportunityTypes();

      actions.ready({ opportunityTypes, recordTypes });
    })();
  }, [actions, api, workspace.id]);

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  const initialValues = mergeValues(
    {
      autoSync: false,
      syncStartDate: '',
      minimumProbability: '0',
      minimumAmount: '0',
      syncInterval: 240,
      opportunityTypes: [],
      recordTypes: [],
      importedMemberAvailability: 'no_availability',
    },
    integration.settings,
  );

  const [recordTypesFilterEnabled, setRecordTypesFilterEnabled] = useState(
    !initialValues.recordTypes?.includes('no_filter'),
  );

  if (!isReady) return null;

  return (
    <Drawer
      isOpen
      title="Sync Settings"
      byline="Salesforce Integration"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit();

            const body = emptyStringToNull({
              ...values,
            });

            await api.www.workspaces(workspace.id).salesforce.updateSyncSettings(body);

            form.save();
            onSave();
            closeDrawer();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              autoSync: Yup.bool().label('Automatically Sync').required('Automatically Sync must be selected'),

              syncInterval: Yup.number()
                .label('Sync Interval')
                .min(60, 'Sync Interval must be at least hourly')
                .max(1440, 'Sync Interval cannot exceed daily')
                .required('Sync Interval is required'),

              minimumProbability: Yup.number()
                .label('Minimum Probability')
                .min(0, 'Minimum Probability cannot be less than 0')
                .max(100, 'Minimum Probability cannot exceed 100')
                .nullable()
                .when('autoSync', {
                  is: true,
                  then: Yup.number().required('Minimum Probability is required when Auto Sync is enabled'),
                  otherwise: Yup.number().notRequired(),
                }),

              minimumAmount: Yup.number()
                .label('Minimum Amount')
                .min(0, 'Minimum Amount cannot be less than 0')
                .max(10000000000000, 'Minimum Amount is too large')
                .nullable(),

              syncStartDate: Yup.date().label('Start Date').nullable().required('Start Date is required'),

              opportunityTypes: Yup.array()
                .label('Opportunity Types')
                .of(Yup.string().nullable())
                .min(1, 'At least one Opportunity Type is required')
                .required('Opportunity Types are required'),

              recordTypes: Yup.array()
                .label('Record Types')
                .of(Yup.string())
                .min(1, 'At least one Record Type is required')
                .required('Record Type selection is required'),

              importedMemberAvailability: Yup.string()
                .label('Imported Member Availability')
                .required('Imported Member Availability is required'),
            })}>
            {(formik) => {
              const { values, setFieldValue } = formik;
              const recordTypeOptions = recordTypes
                ? [
                    ...recordTypes.map((recordType) => ({
                      id: recordType.Id,
                      name: recordType.Name,
                      salesProcessName: recordType.businessProcessName,
                    })),
                    {
                      id: 'no_record_type',
                      name: 'Include Opportunities without a Record Type assigned',
                      salesProcessName: 'Salesforce default in Ruddr',
                    },
                  ]
                : [];
              const recordTypeOptionIds = recordTypeOptions.map((option) => option.id);
              const selectedRecordTypeIds = (values.recordTypes || []).filter((id) => recordTypeOptionIds.includes(id));
              const handleRecordTypeToggle = (recordTypeId, isChecked) => {
                setFieldValue(
                  'recordTypes',
                  isChecked
                    ? selectedRecordTypeIds.filter((id) => id !== recordTypeId)
                    : [...selectedRecordTypeIds, recordTypeId],
                );
              };

              const handleRecordTypesFilterChange = (shouldFilter) => {
                setRecordTypesFilterEnabled(shouldFilter);

                if (shouldFilter) {
                  const filteredTypes = (values.recordTypes || []).filter((value) => value !== 'no_filter');
                  setFieldValue('recordTypes', filteredTypes);
                } else {
                  setFieldValue('recordTypes', ['no_filter']);
                }
              };

              return (
                <Form>
                  {status && <FormMessage.Error>{message}</FormMessage.Error>}
                  <Form.Section
                    title="Automatically Sync"
                    subtitle="If turned on, your Salesforce connection will sync changes to Ruddr at the selected frequency.">
                    <Form.Control>
                      <Field.RadioGroup name="autoSync">
                        <Radio value={true} label="On" />
                        <Radio value={false} label="Off" />
                      </Field.RadioGroup>
                    </Form.Control>
                    <Form.Control>
                      <Field.SingleSelect name="syncInterval" placeholder="Frequency" clearable={false}>
                        <option value={60}>Every Hour</option>
                        <option value={120}>Every Two Hours</option>
                        <option value={180}>Every Three Hours</option>
                        <option value={240}>Every Four Hours</option>
                        <option value={480}>Every Eight Hours</option>
                        <option value={720}>Every Twelve Hours</option>
                        <option value={1440}>Daily</option>
                      </Field.SingleSelect>
                    </Form.Control>
                  </Form.Section>
                  <Form.Section
                    title="Beginning"
                    subtitle="Any opportunity created prior to the Start Date will not be synced.">
                    <Form.Control>
                      <Field.DayPicker name="syncStartDate" placeholder="Start Date" clearable={false} />
                    </Form.Control>
                  </Form.Section>
                  <Form.Section
                    title="Opportunity Minimums"
                    subtitle="Any opportunities with less than these minimum probability and amount values will not be synced.">
                    <Form.Control>
                      <Field.Number
                        name="minimumProbability"
                        placeholder="Minimum Probability"
                        min={0}
                        max={100}
                        precision={2}
                        prefix="%"
                        style={{ textAlign: 'left' }}
                      />
                    </Form.Control>
                    <Form.Control>
                      <Field.Number
                        name="minimumAmount"
                        placeholder="Minimum Amount"
                        min={0}
                        precision={2}
                        prefix="$"
                      />
                    </Form.Control>
                  </Form.Section>
                  {opportunityTypes && (
                    <Form.Section
                      title="Opportunity Types"
                      subtitle="Any opportunities with the selected types will be synced.">
                      <Form.Control>
                        <Field.CheckboxGroup direction="vertical" name="opportunityTypes">
                          {opportunityTypes.map((option, index) => (
                            <Checkbox key={index} value={option} label={option} />
                          ))}
                          <Checkbox key={'null'} value={null} label="Include Opportunities without a Type assigned" />
                        </Field.CheckboxGroup>
                      </Form.Control>
                    </Form.Section>
                  )}
                  {!opportunityTypes && <>Loading opportunity types from Salesforce...</>}
                  {recordTypes && (
                    <Form.Section
                      title="Pipelines"
                      subtitle="Combine your opportunities into one pipeline or split into multiple pipelines by record types.">
                      <Form.Control>
                        <RadioGroup value={recordTypesFilterEnabled} onChange={handleRecordTypesFilterChange}>
                          <Radio value={false} label="One Pipeline" />
                          <Radio value={true} label="Split Pipeline" disabled={recordTypes.length === 0} />
                        </RadioGroup>
                      </Form.Control>
                      {recordTypesFilterEnabled && (
                        <Form.Control>
                          <Table small>
                            <Table.BoxHeader>
                              <Table.Column width="3rem">
                                <StyledCheckbox />
                              </Table.Column>
                              <Table.Column>Record Type</Table.Column>
                              <Table.Column>Sales Process</Table.Column>
                            </Table.BoxHeader>
                            <Table.Body>
                              {recordTypeOptions.map((recordType) => {
                                const isChecked = selectedRecordTypeIds.includes(recordType.id);
                                return (
                                  <Table.Row key={recordType.id}>
                                    <Table.Cell>
                                      <StyledCheckbox>
                                        <Checkbox
                                          checked={isChecked}
                                          onChange={() => handleRecordTypeToggle(recordType.id, isChecked)}
                                        />
                                      </StyledCheckbox>
                                    </Table.Cell>
                                    <Table.Cell>{recordType.name}</Table.Cell>
                                    <Table.Cell>{recordType.salesProcessName || ''}</Table.Cell>
                                  </Table.Row>
                                );
                              })}
                            </Table.Body>
                          </Table>
                        </Form.Control>
                      )}
                    </Form.Section>
                  )}
                  {!recordTypes && <>Loading record types from Salesforce...</>}
                  <Form.Section
                    title="Imported Member Availability"
                    subtitle="Availability settings to apply to imported members.">
                    <Form.Control>
                      <Field.SingleSelect name="importedMemberAvailability" placeholder="Imported Member Availability">
                        <option value="workspace_default">Workspace Default</option>
                        <option value="no_availability">No Availability</option>
                      </Field.SingleSelect>
                    </Form.Control>
                  </Form.Section>
                  <Drawer.Actions>
                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                      <Button isLoading={isSubmitting} onClick={formik.submitForm}>
                        Save &amp; Close
                      </Button>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}
